import { useState, useEffect } from 'react';

/**
 * Cookie Consent Banner Component - Professional Design
 * GDPR/CCPA compliant consent management for analytics and advertising
 */
const CookieConsent = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Check if user has already made a choice
        const consent = localStorage.getItem('cookieConsent');
        if (!consent) {
            // Delay showing banner for smooth entrance animation
            setTimeout(() => {
                setShowBanner(true);
                setTimeout(() => setIsVisible(true), 50);
            }, 1000);
        } else {
            // Apply saved preferences
            const preferences = JSON.parse(consent);
            applyConsent(preferences);
        }
    }, []);

    const applyConsent = (preferences) => {
        // Enable/disable Google Analytics
        if (preferences.analytics && window.gtag) {
            window.gtag('consent', 'update', {
                'analytics_storage': 'granted'
            });
        }

        // Enable/disable Google AdSense
        if (preferences.advertising && window.gtag) {
            window.gtag('consent', 'update', {
                'ad_storage': 'granted',
                'ad_user_data': 'granted',
                'ad_personalization': 'granted'
            });
        }
    };

    const handleAcceptAll = () => {
        const preferences = {
            analytics: true,
            advertising: true,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem('cookieConsent', JSON.stringify(preferences));
        applyConsent(preferences);
        closeBanner();
    };

    const handleRejectAll = () => {
        const preferences = {
            analytics: false,
            advertising: false,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem('cookieConsent', JSON.stringify(preferences));
        closeBanner();
    };

    const handleSavePreferences = (analytics, advertising) => {
        const preferences = {
            analytics,
            advertising,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem('cookieConsent', JSON.stringify(preferences));
        applyConsent(preferences);
        closeBanner();
    };

    const closeBanner = () => {
        setIsVisible(false);
        setTimeout(() => {
            setShowBanner(false);
            setShowSettings(false);
        }, 300);
    };

    if (!showBanner) return null;

    return (
        <>
            {/* Backdrop with blur */}
            <div
                className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'
                    }`}
                onClick={closeBanner}
            />

            {/* Banner Container */}
            <div className="fixed bottom-0 left-0 right-0 z-[9999] p-3 sm:p-4 md:p-6 pointer-events-none">
                <div
                    className={`max-w-5xl mx-auto pointer-events-auto transition-all duration-500 ease-out ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                        }`}
                >
                    <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] border border-gray-200/50 overflow-hidden">
                        {!showSettings ? (
                            // Main Banner - Premium Design
                            <div className="relative">
                                {/* Gradient accent bar */}
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500" />

                                <div className="p-6 sm:p-8 md:p-10">
                                    <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                                        {/* Icon */}
                                        <div className="flex-shrink-0">
                                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
                                                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1">
                                            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 leading-tight">
                                                Your Privacy Matters
                                            </h3>
                                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-6">
                                                We use cookies to enhance your browsing experience, analyze site traffic, and deliver personalized content.
                                                Your data is protected and you're in control.{' '}
                                                <a
                                                    href="/privacy"
                                                    className="text-primary-600 hover:text-primary-700 font-medium underline decoration-2 underline-offset-2 transition-colors"
                                                >
                                                    Privacy Policy
                                                </a>
                                            </p>

                                            {/* Action Buttons */}
                                            <div className="flex flex-col sm:flex-row gap-3">
                                                <button
                                                    onClick={handleAcceptAll}
                                                    className="group relative px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden"
                                                >
                                                    <span className="relative z-10 flex items-center justify-center gap-2">
                                                        Accept All
                                                        <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </span>
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                                                </button>

                                                <button
                                                    onClick={handleRejectAll}
                                                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-xl transition-all duration-200 border-2 border-gray-200 hover:border-gray-300"
                                                >
                                                    Reject All
                                                </button>

                                                <button
                                                    onClick={() => setShowSettings(true)}
                                                    className="px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-xl transition-all duration-200 border-2 border-gray-300 hover:border-gray-400 flex items-center justify-center gap-2"
                                                >
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    Customize
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            // Settings Panel - Premium Design
                            <ConsentSettings
                                onSave={handleSavePreferences}
                                onBack={() => setShowSettings(false)}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

/**
 * Consent Settings Component - Professional Design
 */
const ConsentSettings = ({ onSave, onBack }) => {
    const [analytics, setAnalytics] = useState(true);
    const [advertising, setAdvertising] = useState(true);

    return (
        <div className="relative">
            {/* Gradient accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500" />

            <div className="p-6 sm:p-8 md:p-10">
                {/* Back Button */}
                <button
                    onClick={onBack}
                    className="mb-6 text-primary-600 hover:text-primary-700 font-semibold flex items-center gap-2 group transition-colors"
                >
                    <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Overview
                </button>

                {/* Header */}
                <div className="mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        Privacy Preferences
                    </h3>
                    <p className="text-gray-600">
                        Choose which cookies you want to allow. You can change these settings anytime.
                    </p>
                </div>

                {/* Cookie Categories */}
                <div className="space-y-4 mb-8">
                    {/* Essential Cookies */}
                    <div className="group relative p-5 bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-xl border-2 border-gray-200 transition-all">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                    <h4 className="font-bold text-gray-900">Essential Cookies</h4>
                                </div>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    Required for the website to function properly. These cookies enable core functionality and cannot be disabled.
                                </p>
                            </div>
                            <div className="flex-shrink-0">
                                <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-700 text-sm font-bold rounded-lg">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    Always Active
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Analytics Cookies */}
                    <div className={`group relative p-5 rounded-xl border-2 transition-all ${analytics
                            ? 'bg-gradient-to-br from-primary-50 to-primary-100/30 border-primary-300 shadow-sm'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <h4 className="font-bold text-gray-900">Analytics Cookies</h4>
                                </div>
                                <p className="text-sm text-gray-600 leading-relaxed mb-2">
                                    Help us understand how visitors interact with our website by collecting anonymous data (Google Analytics).
                                </p>
                                <p className="text-xs text-gray-500 italic">
                                    Used for: Traffic analysis, user behavior, performance metrics
                                </p>
                            </div>
                            <div className="flex-shrink-0">
                                <ToggleSwitch checked={analytics} onChange={setAnalytics} />
                            </div>
                        </div>
                    </div>

                    {/* Advertising Cookies */}
                    <div className={`group relative p-5 rounded-xl border-2 transition-all ${advertising
                            ? 'bg-gradient-to-br from-accent-50 to-accent-100/30 border-accent-300 shadow-sm'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <svg className="w-5 h-5 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                                    </svg>
                                    <h4 className="font-bold text-gray-900">Advertising Cookies</h4>
                                </div>
                                <p className="text-sm text-gray-600 leading-relaxed mb-2">
                                    Used to deliver personalized ads and measure advertising campaign performance (Google AdSense).
                                </p>
                                <p className="text-xs text-gray-500 italic">
                                    Used for: Personalized ads, ad performance, remarketing
                                </p>
                            </div>
                            <div className="flex-shrink-0">
                                <ToggleSwitch checked={advertising} onChange={setAdvertising} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Save Button */}
                <button
                    onClick={() => onSave(analytics, advertising)}
                    className="group relative w-full px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden"
                >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                        Save My Preferences
                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                </button>
            </div>
        </div>
    );
};

/**
 * Premium Toggle Switch Component
 */
const ToggleSwitch = ({ checked, onChange }) => {
    return (
        <button
            onClick={() => onChange(!checked)}
            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-primary-300 ${checked ? 'bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg' : 'bg-gray-300'
                }`}
        >
            <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-all duration-300 ${checked ? 'translate-x-7 scale-110' : 'translate-x-1'
                    }`}
            >
                {checked && (
                    <svg className="w-6 h-6 text-primary-600 p-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                )}
            </span>
        </button>
    );
};

export default CookieConsent;
